<template>
  <v-sheet outlined class="rounded overflow-hidden reminder-panel" color="bg1">
    <div class="reminder-panel__header">
      <div>
        <div class="ds-dialog__title">Deploy reminder</div>
        <div class="reminder-panel__subtitle">
          The nudge that chases the actual app deployment once these notes are out.
        </div>
      </div>
      <v-spacer />
      <v-btn icon small :loading="loading" title="Refresh" @click="fetchReminders">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-divider />

    <div class="reminder-panel__body">
      <!-- Pending reminder ---------------------------------------------------------------- -->
      <template v-if="pendingReminder">
        <div class="reminder-panel__row">
          <v-icon small class="mr-2">mdi-calendar-clock</v-icon>
          <div>
            <div class="reminder-panel__due">{{ dueLabel }}</div>
            <div class="reminder-panel__relative">{{ dueRelative }}</div>
          </div>
          <v-spacer />
          <v-chip x-small :color="statusMeta.color" text-color="white">
            {{ statusMeta.label }}
          </v-chip>
        </div>

        <div class="reminder-panel__row reminder-panel__row--muted">
          <v-icon small class="mr-2">mdi-account-multiple-outline</v-icon>
          <span>
            {{ recipientCount }}
            {{ recipientCount === 1 ? 'recipient' : 'recipients' }}
          </span>
        </div>

        <div v-if="canManage" class="reminder-panel__actions">
          <v-btn small outlined :disabled="isBusy" @click="openReschedule">
            <v-icon left small>mdi-calendar-edit</v-icon>
            Reschedule
          </v-btn>
          <v-btn small outlined color="primary" :disabled="isBusy" @click="sendNow">
            <v-icon left small>mdi-send-outline</v-icon>
            Send now
          </v-btn>
          <v-spacer />
          <v-btn small text color="error" :disabled="isBusy" @click="cancelReminder">
            <v-icon left small>mdi-bell-off-outline</v-icon>
            Cancel reminder
          </v-btn>
        </div>
      </template>

      <!-- Published, but nothing pending --------------------------------------------------- -->
      <div v-else-if="isPublished" class="ds-info-box ds-info-box--info">
        <v-icon class="ds-info-box__icon" small>mdi-bell-off-outline</v-icon>
        <div class="ds-info-box__content">
          <strong>No reminder scheduled.</strong>
          A deploy reminder can only be armed at publish time — there is no endpoint yet for arming
          one against a release that is already published. To get a reminder for these notes,
          re-notify the release and tick “Remind release owners to deploy” in that dialog.
        </div>
      </div>

      <!-- Not published yet ----------------------------------------------------------------- -->
      <div v-else class="ds-info-box ds-info-box--info">
        <v-icon class="ds-info-box__icon" small>mdi-information-outline</v-icon>
        <div class="ds-info-box__content">
          This release has not been published yet. You can arm a deploy reminder when you publish it
          — the publish dialog has the toggle and the delay settings.
        </div>
      </div>
    </div>

    <!-- Reschedule dialog ------------------------------------------------------------------- -->
    <v-dialog v-model="rescheduleOpen" persistent max-width="420">
      <v-card class="ds-dialog" color="bg1">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary" size="20">mdi-calendar-edit</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <div class="ds-dialog__title">Reschedule reminder</div>
            <div class="ds-dialog__subtitle">Pick a new date and time in your local timezone.</div>
          </div>
        </div>

        <div class="ds-dialog__body reminder-panel__picker-body">
          <v-date-picker
            v-model="rescheduleDate"
            :min="minDate"
            no-title
            full-width
            color="primary" />
          <v-time-picker
            v-model="rescheduleTime"
            format="24hr"
            full-width
            color="primary"
            class="mt-3" />
          <v-alert v-if="rescheduleError" dense outlined type="error" class="mt-3 mb-0">
            {{ rescheduleError }}
          </v-alert>
        </div>

        <div class="ds-dialog__footer">
          <div class="ds-dialog__hint">Saved as {{ reschedulePreview }}</div>
          <v-spacer />
          <v-btn text small :disabled="isBusy" @click="rescheduleOpen = false">Cancel</v-btn>
          <v-btn color="primary" small :loading="isBusy" @click="saveReschedule">Save</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import { $formatDate, $has } from '@Utils';
  import { warn } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import {
    ReleaseReminderStatus,
    ReleaseReminderStatusMeta,
    ReleaseStatus,
  } from '@ReleaseNotes/config/enums';

  dayjs.extend(relativeTime);

  const UNKNOWN_STATUS = { label: 'Unknown', color: 'grey' };

  /**
   * ReminderPanel — shows and manages the single pending deploy reminder for a release.
   *
   * Known gap (plan §9), deliberately NOT worked around client-side: a reminder can only be armed
   * as part of a `publish` call. There is no standalone "arm a reminder" endpoint, so the empty
   * state says so in plain words rather than offering a button that would 404.
   *
   * Timezone: `dueAt` is sent as `new Date(...).toISOString()` — i.e. an ISO 8601 instant in UTC
   * with a `Z` suffix. The pickers collect a wall-clock date and time, which the browser resolves
   * in the user's local zone; converting to UTC before transport removes any ambiguity about which
   * zone the server should assume, and the server stores instants rather than local times.
   */
  export default {
    name: 'ReminderPanel',

    props: {
      release: { type: Object, default: null },
      releaseId: { type: [String, Number], default: null },
    },

    data() {
      return {
        permissions,
        rescheduleOpen: false,
        rescheduleDate: '',
        rescheduleTime: '09:00',
        rescheduleError: '',
        saving: false,
      };
    },

    computed: {
      loading() {
        return this.$store.getters['RELEASE_NOTES/IS_REMINDERS_LOADING'];
      },

      isBusy() {
        return this.saving || this.loading;
      },

      canManage() {
        return $has(permissions.ReleaseNotes.Publish);
      },

      isPublished() {
        return !!this.release && this.release.status === ReleaseStatus.Published;
      },

      reminders() {
        const list = this.$store.getters['RELEASE_NOTES/GET_REMINDERS'];
        return Array.isArray(list) ? list : [];
      },

      /**
       * The reminder that is still going to fire. The release detail DTO usually carries it
       * inline; the reminders endpoint is the fallback. Both are probed defensively.
       */
      pendingReminder() {
        const inline =
          (this.release && (this.release.pendingReminder || this.release.reminder)) || null;
        if (inline && this.isPending(inline)) return inline;
        return this.reminders.find((reminder) => this.isPending(reminder)) || null;
      },

      reminderId() {
        const reminder = this.pendingReminder;
        if (!reminder) return null;
        return [reminder.id, reminder.reminderId].find((value) => value != null);
      },

      dueAt() {
        const reminder = this.pendingReminder || {};
        return [reminder.dueAt, reminder.dueAtUtc, reminder.scheduledFor].find((value) => !!value);
      },

      dueLabel() {
        return this.dueAt ? $formatDate(this.dueAt, 'DD/MM/YYYY HH:mm') : 'No due date set';
      },

      dueRelative() {
        if (!this.dueAt) return '';
        const parsed = dayjs(this.dueAt);
        return parsed.isValid() ? parsed.fromNow() : '';
      },

      statusMeta() {
        const reminder = this.pendingReminder || {};
        return ReleaseReminderStatusMeta[reminder.status] || UNKNOWN_STATUS;
      },

      recipientCount() {
        const reminder = this.pendingReminder || {};
        const candidates = [reminder.recipientCount, reminder.recipientsCount];
        const found = candidates.find((value) => Number.isFinite(Number(value)) && value !== null);
        if (found !== undefined) return Number(found);
        const emails = reminder.recipientEmails || reminder.recipients;
        return Array.isArray(emails) ? emails.length : 0;
      },

      minDate() {
        return dayjs().format('YYYY-MM-DD');
      },

      /** The instant the pickers currently describe, or null when incomplete/invalid. */
      rescheduleInstant() {
        if (!this.rescheduleDate) return null;
        const time = this.rescheduleTime || '00:00';
        const parsed = dayjs(`${this.rescheduleDate}T${time}`);
        return parsed.isValid() ? parsed : null;
      },

      reschedulePreview() {
        const instant = this.rescheduleInstant;
        return instant ? `${instant.format('DD/MM/YYYY HH:mm')} (your local time)` : '—';
      },
    },

    watch: {
      releaseId() {
        this.fetchReminders();
      },
    },

    created() {
      this.fetchReminders();
    },

    methods: {
      $has,
      $formatDate,

      /**
       * True for a reminder that has not yet fired or been cancelled.
       * @param {object} reminder
       */
      isPending(reminder) {
        if (!reminder) return false;
        const status = reminder.status;
        if (status === undefined || status === null) return true;
        return (
          status === ReleaseReminderStatus.Scheduled || status === ReleaseReminderStatus.Sending
        );
      },

      async fetchReminders() {
        if (this.releaseId == null) return;
        try {
          await this.$store.dispatch('RELEASE_NOTES/FETCH_REMINDERS', this.releaseId);
        } catch (err) {
          warn('[ReminderPanel] failed to load reminders', err);
        }
      },

      openReschedule() {
        const base = this.dueAt ? dayjs(this.dueAt) : dayjs().add(1, 'day');
        const seed = base.isValid() && base.isAfter(dayjs()) ? base : dayjs().add(1, 'day');
        this.rescheduleDate = seed.format('YYYY-MM-DD');
        this.rescheduleTime = seed.format('HH:mm');
        this.rescheduleError = '';
        this.rescheduleOpen = true;
      },

      async saveReschedule() {
        const instant = this.rescheduleInstant;
        if (!instant) {
          this.rescheduleError = 'Pick both a date and a time.';
          return;
        }
        // The date picker's `min` blocks past DAYS; this also blocks a past time on today.
        if (!instant.isAfter(dayjs())) {
          this.rescheduleError = 'Pick a date and time in the future.';
          return;
        }
        if (this.reminderId == null) return;

        this.rescheduleError = '';
        this.saving = true;
        try {
          await this.$store.dispatch('RELEASE_NOTES/UPDATE_REMINDER', {
            id: this.releaseId,
            reminderId: this.reminderId,
            data: { dueAt: instant.toDate().toISOString() },
          });
          this.rescheduleOpen = false;
          this.$dialog.notify.success('Reminder rescheduled', {
            position: 'top-right',
            timeout: 3000,
          });
          await this.fetchReminders();
          this.$emit('changed');
        } catch (err) {
          warn('[ReminderPanel] reschedule rejected', err);
        } finally {
          this.saving = false;
        }
      },

      async cancelReminder() {
        if (this.reminderId == null) return;

        const confirmed = await this.$dialog.confirm({
          title: 'Cancel reminder',
          text: 'The deploy reminder will not be sent. This cannot be undone — a new reminder can only be armed by publishing again.',
          actions: {
            false: 'Keep it',
            true: { text: 'Cancel reminder', color: 'error' },
          },
        });
        if (!confirmed) return;

        this.saving = true;
        try {
          await this.$store.dispatch('RELEASE_NOTES/CANCEL_REMINDER', {
            id: this.releaseId,
            reminderId: this.reminderId,
          });
          this.$dialog.notify.success('Reminder cancelled', {
            position: 'top-right',
            timeout: 3000,
          });
          await this.fetchReminders();
          this.$emit('changed');
        } catch (err) {
          warn('[ReminderPanel] cancel rejected', err);
        } finally {
          this.saving = false;
        }
      },

      async sendNow() {
        if (this.reminderId == null) return;

        const confirmed = await this.$dialog.confirm({
          title: 'Send reminder now',
          text: 'Sends the deploy reminder immediately instead of waiting for its due date.',
          actions: {
            false: 'Cancel',
            true: { text: 'Send now', color: 'primary' },
          },
        });
        if (!confirmed) return;

        this.saving = true;
        try {
          await this.$store.dispatch('RELEASE_NOTES/SEND_REMINDER_NOW', {
            id: this.releaseId,
            reminderId: this.reminderId,
          });
          this.$dialog.notify.success('Reminder sent', { position: 'top-right', timeout: 3000 });
          await this.fetchReminders();
          this.$emit('changed');
        } catch (err) {
          warn('[ReminderPanel] send-now rejected', err);
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .reminder-panel__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
  }

  .reminder-panel__subtitle {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: 2px;
  }

  .reminder-panel__body {
    padding: 16px;
  }

  .reminder-panel__row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    &--muted {
      font-size: 0.8125rem;
      opacity: 0.75;
    }
  }

  .reminder-panel__due {
    font-weight: 600;
  }

  .reminder-panel__relative {
    font-size: 0.75rem;
    opacity: 0.65;
  }

  .reminder-panel__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 14px;
  }

  .reminder-panel__picker-body {
    padding-top: 12px;
  }
</style>
